import { Group, Pouch, Sheet } from '../types';
import { getPouchContributionForTotals } from '../logic/BalancingPouchLogic';
import { groupShowsTimeConversion, shouldShowTotalLabel } from '../logic/GroupLogic';
import {
  getSortedLayers,
  mirrorPouchesForLayer,
  resolveActiveLayerId,
} from '../logic/LayerLogic';
import {
  buildGroupSubtotalLayerTooltipLines,
  formatGroupSubtotalPlainVisible,
  resolveLayerNumericTooltipHtml,
} from './layerValueTooltip';

export interface GroupDisplayTotals {
  contribution: number;
  plainVisible: string;
  valueTooltipHtml: string | undefined;
  isTotalGroup: boolean;
  showTimeConversion: boolean;
}

function isPouchMuted(pouchId: string, allGroups: Group[]): boolean {
  return allGroups.some(g => g.color === 'mute' && g.pouches?.includes(pouchId));
}

export function computeGroupSubtotalContribution(
  group: Group,
  pouches: Pouch[],
  allGroups: Group[],
  balancingPouchId?: string | null,
): number {
  const groupPouches = pouches.filter(
    p =>
      group.pouches?.includes(p.id) &&
      !p._isDeleting &&
      !p._isGtpRenderPreview &&
      !isPouchMuted(p.id, allGroups)
  );
  return groupPouches.reduce(
    (total, pouch) =>
      total +
      getPouchContributionForTotals(
        pouch,
        allGroups,
        pouches,
        group.id,
        balancingPouchId,
      ),
    0
  );
}

export { groupShowsTimeConversion, formatGroupSubtotalPlainVisible };

function buildMirroredByLayer(
  sheet: Sheet,
  pouches: Pouch[],
  allGroups: Group[]
): Map<string, Pouch[]> {
  const map = new Map<string, Pouch[]>();
  getSortedLayers(sheet).forEach(layer => {
    map.set(layer.id, mirrorPouchesForLayer(pouches, layer.id, sheet, allGroups));
  });
  return map;
}

function computeOneGroupDisplayTotals(
  group: Group,
  pouches: Pouch[],
  allGroups: Group[],
  currentSheet: Sheet | undefined,
  mirroredByLayer: Map<string, Pouch[]> | undefined
): GroupDisplayTotals {
  const contribution = computeGroupSubtotalContribution(
    group,
    pouches,
    allGroups,
    currentSheet?.balancingPouchId,
  );
  const showTimeConversion = groupShowsTimeConversion(
    group,
    pouches,
    allGroups,
    currentSheet?.balancingPouchId
  );
  const plainVisible = formatGroupSubtotalPlainVisible(contribution, showTimeConversion);

  let valueTooltipHtml: string | undefined;
  if (!currentSheet?.layers?.length) {
    valueTooltipHtml = resolveLayerNumericTooltipHtml(currentSheet, plainVisible, [
      plainVisible,
    ]);
  } else {
    const layerLines = buildGroupSubtotalLayerTooltipLines(
      currentSheet,
      group,
      pouches,
      allGroups,
      mirroredByLayer
    );
    valueTooltipHtml = resolveLayerNumericTooltipHtml(
      currentSheet,
      plainVisible,
      layerLines
    );
  }

  return {
    contribution,
    plainVisible,
    valueTooltipHtml,
    isTotalGroup: shouldShowTotalLabel(group, allGroups, pouches),
    showTimeConversion,
  };
}

/** シート内全グループの小計表示を一括計算（レイヤー mirror はレイヤー数回のみ） */
export function computeAllGroupDisplayTotals(
  groups: Group[],
  pouches: Pouch[],
  allGroups: Group[],
  currentSheet: Sheet | undefined
): Record<string, GroupDisplayTotals> {
  const mirroredByLayer =
    currentSheet?.layers?.length && currentSheet.layers.length > 0
      ? buildMirroredByLayer(currentSheet, pouches, allGroups)
      : undefined;

  const activeLayerId = currentSheet ? resolveActiveLayerId(currentSheet) : undefined;
  const pouchesForContribution =
    activeLayerId && mirroredByLayer
      ? mirroredByLayer.get(activeLayerId) ?? pouches
      : pouches;

  const out: Record<string, GroupDisplayTotals> = {};
  groups.forEach(group => {
    out[group.id] = computeOneGroupDisplayTotals(
      group,
      pouchesForContribution,
      allGroups,
      currentSheet,
      mirroredByLayer
    );
  });
  return out;
}
